/**
 * Subtle motion for things a page shows and hides — shared so every module eases the same way.
 *
 *   Collapse        a section the view shows or hides: height and opacity, both ways;
 *   AutoHeight      a box that eases to its content's height (a dialog whose tab changes);
 *   useItemMotion   items a filter adds, removes or reorders: they fade in, fade out (as ghosts,
 *                   since React has already removed them) and, in flow layouts, glide to their
 *                   new places (FLIP);
 *   useLast         keep a value on screen while it animates out;
 *   fadeIn          the one-off rise-and-fade the others use.
 *
 * Everything respects prefers-reduced-motion, and nothing relies on a fill that could leave an
 * element stuck invisible: if frames are paused (a background tab) the animations just don't
 * play, and the resting state is always the real one.
 */
import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { cn } from './utils';

export const reducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const EASE = 'cubic-bezier(0.4,0,0.2,1)';

/**
 * A short rise-and-fade for something that has just appeared. The keyframe needs `offset: 0`: a
 * lone keyframe is otherwise the END state, which fades the item out and then snaps it back.
 * `fill: 'backwards'` holds it invisible until the animation starts, so the first frame can't
 * show it at full strength; nothing holds afterwards, so it can't stick.
 */
export function fadeIn(element: HTMLElement | null): void {
  if (!element || reducedMotion()) return;
  element.animate([{ opacity: 0, transform: 'translateY(4px)', offset: 0 }], {
    duration: 220,
    easing: 'ease-out',
    fill: 'backwards',
  });
}

/** The value, or — once it goes null — the last non-null one, so it can animate out. */
export function useLast<T>(value: T | null): T | null {
  const [last, setLast] = useState(value);
  if (value !== null && value !== last) setLast(value);
  return value ?? last;
}

/** The content box's height, kept current by a ResizeObserver; null until first measured. */
function useMeasuredHeight(): [RefObject<HTMLDivElement | null>, number | null] {
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = inner.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [inner, height];
}

/**
 * Shows or hides its content by easing its height between 0 and the content's measured height,
 * with the opacity leading on the way out and trailing on the way in, so nothing flashes at full
 * strength while it is still small. The content stays mounted while closed, inert and hidden from
 * assistive tech.
 *
 * A pixel height, not a grid row going 0fr → 1fr: fr tracks don't interpolate linearly in an
 * auto-height grid, so the row collapses almost at once and pops at the end. The box clips with
 * `overflow: clip`, not `hidden`, which would make it a scroll container and break any sticky
 * content inside.
 */
export function Collapse({
  open,
  children,
  className,
  duration = 300,
}: {
  open: boolean;
  children: ReactNode;
  /** On the content — e.g. the spacing that should collapse with it. */
  className?: string;
  /** Of the height; the fade scales with it. */
  duration?: number;
}) {
  const [inner, height] = useMeasuredHeight();
  const fade = Math.round(duration * 0.6);
  return (
    <div
      className="overflow-clip motion-reduce:!transition-none"
      style={{
        height: open ? (height ?? 'auto') : 0,
        opacity: open ? 1 : 0,
        transition: open
          ? `height ${duration}ms ${EASE}, opacity ${fade}ms ease-out ${duration - fade}ms`
          : `height ${duration}ms ${EASE}, opacity ${Math.round(duration * 0.5)}ms ease-out`,
      }}
      inert={!open}
      aria-hidden={!open}
    >
      <div ref={inner} className={className}>
        {children}
      </div>
    </div>
  );
}

/**
 * Eases its height to fit its content, so a dialog grows or shrinks smoothly when a tab or a
 * sub-option swaps the fields inside it, and fades the content in when `swapKey` changes.
 *
 * The first measurement only replaces `auto` with the same number, so nothing animates on open.
 * A 4px inset inside the clipping box keeps focus rings at the edges visible.
 */
export function AutoHeight({ swapKey, children }: { swapKey: string; children: ReactNode }) {
  const [inner, height] = useMeasuredHeight();
  const lastKey = useRef(swapKey);

  useLayoutEffect(() => {
    if (lastKey.current === swapKey) return;
    lastKey.current = swapKey;
    if (reducedMotion() || !inner.current) return;
    inner.current.animate([{ opacity: 0, offset: 0 }], {
      duration: 200,
      easing: 'ease-out',
      fill: 'backwards',
    });
  }, [swapKey, inner]);

  return (
    <div
      className={cn(
        '-m-1 overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none',
      )}
      style={{ height: height ?? 'auto' }}
    >
      <div ref={inner} className="p-1">
        {children}
      </div>
    </div>
  );
}

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Drawn {
  element: HTMLElement;
  parent: HTMLElement;
  box: Box;
}

/** Where an element sits in the root's content (scroll-independent), as drawn right now. */
function boxIn(element: HTMLElement, root: HTMLElement): Box {
  const rect = element.getBoundingClientRect();
  const base = root.getBoundingClientRect();
  return {
    x: rect.left - base.left - root.clientLeft + root.scrollLeft,
    y: rect.top - base.top - root.clientTop + root.scrollTop,
    width: rect.width,
    height: rect.height,
  };
}

/** The three beats of a morph, in ms: contents out, box reshape, new item in. */
const MORPH = { out: 70, reshape: 320, in: 100 } as const;

/** A clone of `element`, pinned absolutely in `root` at `box`, inert and out of the a11y tree. */
function ghostOf(element: HTMLElement, root: HTMLElement, box: Box): HTMLElement {
  const ghost = element.cloneNode(true) as HTMLElement;
  ghost.removeAttribute('data-motion');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  Object.assign(ghost.style, {
    position: 'absolute',
    left: `${box.x}px`,
    top: `${box.y}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    margin: '0',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    // A cloned <li> outside its list would draw a bullet.
    listStyle: 'none',
    // Above the columns it floats over; a morph lifts the incoming item above it (see morphAll).
    zIndex: '1',
  });
  root.appendChild(ghost);
  return ghost;
}

/**
 * Plays a morph for every item that exists on both sides of the switch (see `useItemMotion`);
 * items on one side only simply fade out or in.
 */
function morphAll(
  root: HTMLElement,
  previous: Map<string, Drawn>,
  next: Map<string, Drawn>,
  exits: Map<string, Animation>,
): void {
  const total = MORPH.out + MORPH.reshape + MORPH.in;
  const before = new Map<string, Drawn>();
  for (const drawnItem of previous.values()) {
    const id = drawnItem.element.dataset.morph;
    if (id) before.set(id, drawnItem);
  }
  const matched = new Set<string>();

  for (const item of next.values()) {
    const id = item.element.dataset.morph;
    const old = id ? before.get(id) : undefined;
    if (!id || !old) {
      fadeIn(item.element);
      continue;
    }
    matched.add(id);
    const ghost = ghostOf(old.element, root, old.box);
    const box = ghost.matches('[data-morph-box]')
      ? ghost
      : (ghost.querySelector<HTMLElement>('[data-morph-box]') ?? ghost);
    // The box fills the ghost as it reshapes; its contents are faded out first, and clipped.
    Object.assign(box.style, { width: '100%', height: '100%', overflow: 'hidden' });
    for (const child of box.children)
      (child as HTMLElement).animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: MORPH.out,
        easing: 'ease-out',
        fill: 'forwards',
      });
    ghost.animate(
      [
        {
          left: `${old.box.x}px`,
          top: `${old.box.y}px`,
          width: `${old.box.width}px`,
          height: `${old.box.height}px`,
        },
        {
          left: `${item.box.x}px`,
          top: `${item.box.y}px`,
          width: `${item.box.width}px`,
          height: `${item.box.height}px`,
        },
      ],
      { delay: MORPH.out - 20, duration: MORPH.reshape, easing: EASE, fill: 'both' },
    );
    // The ghost box stays until the new item has faded in over it, then goes.
    const hold = (MORPH.out + MORPH.reshape) / total;
    const exit = ghost.animate([{ opacity: 1 }, { opacity: 1, offset: hold }, { opacity: 0 }], {
      duration: total,
    });
    exit.addEventListener('finish', () => ghost.remove());
    exit.addEventListener('cancel', () => ghost.remove());
    // Lift the incoming item over its ghost box while it fades in, then put it back.
    const { position, zIndex } = item.element.style;
    if (getComputedStyle(item.element).position === 'static')
      item.element.style.position = 'relative';
    item.element.style.zIndex = '2';
    const arrive = item.element.animate(
      [{ opacity: 0 }, { opacity: 0, offset: hold }, { opacity: 1 }],
      {
        duration: total,
        easing: 'ease-out',
      },
    );
    const settle = () => Object.assign(item.element.style, { position, zIndex });
    arrive.addEventListener('finish', settle);
    arrive.addEventListener('cancel', settle);
  }

  for (const [key, gone] of previous) {
    const id = gone.element.dataset.morph;
    if (id && matched.has(id)) continue;
    exits.get(key)?.cancel();
    const ghost = ghostOf(gone.element, root, gone.box);
    const exit = ghost.animate([{ opacity: 0, transform: 'translateY(4px)', offset: 1 }], {
      duration: 180,
      easing: 'ease-in',
    });
    exit.addEventListener('finish', () => ghost.remove());
    exit.addEventListener('cancel', () => ghost.remove());
  }
}

/**
 * Animates the items inside `root` that carry `data-motion="<stable key>"`, across renders:
 *
 *   - an item that appears rises and fades in;
 *   - an item that goes away leaves a ghost — a clone of its last DOM node, inert and
 *     untouchable — that fades out where it was, then removes itself. If the item comes back
 *     before its ghost has gone, the ghost is dropped at once so the two never overlap;
 *   - with `flip`, items that moved glide from where they were drawn to where they are now
 *     (for flow layouts — lists and columns; absolutely positioned items should transition their
 *     own left/top instead). A move that interrupts another starts from wherever the item is
 *     on screen.
 *
 * `ghosts: 'parent'` puts a ghost back into the item's own parent — right for absolutely
 * positioned items. `'root'` positions it absolutely in `root` at the item's last box — right
 * for flow layouts, where re-inserting it into the list would shift the others; `root` must then
 * be a positioned element (e.g. `relative`).
 *
 * Moves only animate when the set or order of keys changed, so a re-render for some other reason
 * (or a layout shift nobody caused, like an image loading) never sets things gliding. The first
 * render draws everything at rest, and so does any render where `still` has changed — for a
 * switch the page doesn't want animated.
 *
 * `morph` is for a switch that redraws the same items in a different shape (cards ↔ rows). When
 * it changes, items are matched across the switch by `data-morph="<id>"` and each one plays in
 * three beats: its old contents fade out, its box (a ghost, keeping the old look) reshapes and
 * moves to the new item's place, then the new item fades in over it. The element that draws the
 * box is the one marked `data-morph-box` (else the item itself); its children are the contents.
 * Needs `ghosts: 'root'`.
 */
export function useItemMotion(
  root: RefObject<HTMLElement | null>,
  {
    flip = false,
    ghosts = 'parent',
    still,
    morph,
  }: { flip?: boolean; ghosts?: 'parent' | 'root'; still?: string; morph?: string } = {},
): void {
  const drawn = useRef<Map<string, Drawn> | null>(null);
  const lastStill = useRef(still);
  const lastMorph = useRef(morph);
  const signature = useRef('');
  const exits = useRef(new Map<string, Animation>());
  const moves = useRef(new Map<string, Animation>());

  useLayoutEffect(() => {
    const container = root.current;
    if (!container) return;
    const elements = [...container.querySelectorAll<HTMLElement>('[data-motion]')];
    const keys = elements.map((element) => element.dataset.motion!);
    const changed = keys.join('|') !== signature.current;
    signature.current = keys.join('|');
    const previous = drawn.current;
    const switched = still !== lastStill.current;
    lastStill.current = still;
    const morphing = morph !== lastMorph.current;
    lastMorph.current = morph;
    const animate = previous !== null && !switched && !reducedMotion();
    const next = new Map<string, Drawn>();

    if (morphing) {
      for (const element of elements)
        next.set(element.dataset.motion!, {
          element,
          parent: element.parentElement!,
          box: boxIn(element, container),
        });
      if (animate && previous) morphAll(container, previous, next, exits.current);
      drawn.current = next;
      return;
    }

    elements.forEach((element, i) => {
      const key = keys[i]!;
      const before = previous?.get(key);
      if (!animate || !changed) {
        next.set(key, { element, parent: element.parentElement!, box: boxIn(element, container) });
        return;
      }
      if (!before) {
        // Back before its exit finished: drop the fading copy so the two never overlap.
        exits.current.get(key)?.cancel();
        next.set(key, { element, parent: element.parentElement!, box: boxIn(element, container) });
        fadeIn(element);
        return;
      }
      if (!flip) {
        next.set(key, { element, parent: element.parentElement!, box: boxIn(element, container) });
        return;
      }
      // Where it is drawn now (a running move included), then where layout puts it.
      const shown = boxIn(element, container);
      const running = moves.current.get(key);
      running?.cancel();
      const settled = boxIn(element, container);
      // Drawn position before this render = its old settled box plus any move still in flight.
      const fromX = before.box.x + (shown.x - settled.x);
      const fromY = before.box.y + (shown.y - settled.y);
      const dx = fromX - settled.x;
      const dy = fromY - settled.y;
      next.set(key, { element, parent: element.parentElement!, box: settled });
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      const move = element.animate(
        [{ transform: `translate(${dx}px, ${dy}px)`, offset: 0 }, { transform: 'none' }],
        { duration: 300, easing: EASE },
      );
      moves.current.set(key, move);
      const forget = () => {
        if (moves.current.get(key) === move) moves.current.delete(key);
      };
      move.addEventListener('finish', forget);
      move.addEventListener('cancel', forget);
    });

    if (animate && changed && previous) {
      for (const [key, gone] of previous) {
        if (next.has(key)) continue;
        // The parent recorded while it was drawn: React has detached the element by now.
        const parent = ghosts === 'root' ? container : gone.parent;
        if (!parent?.isConnected) continue;
        let ghost: HTMLElement;
        if (ghosts === 'root') ghost = ghostOf(gone.element, container, gone.box);
        else {
          ghost = gone.element.cloneNode(true) as HTMLElement;
          ghost.removeAttribute('data-motion');
          ghost.setAttribute('aria-hidden', 'true');
          ghost.inert = true;
          ghost.style.pointerEvents = 'none';
          parent.appendChild(ghost);
        }
        const exit = ghost.animate([{ opacity: 0, transform: 'translateY(4px)', offset: 1 }], {
          duration: 180,
          easing: 'ease-in',
        });
        exits.current.get(key)?.cancel();
        exits.current.set(key, exit);
        const done = () => {
          ghost.remove();
          if (exits.current.get(key) === exit) exits.current.delete(key);
        };
        exit.addEventListener('finish', done);
        exit.addEventListener('cancel', done);
      }
    }
    drawn.current = next;
  });
}
